import { useSelector } from 'react-redux';

import { SubscriptionIntervalTypes } from '../../utils/constants';
import { getFableticsEntitlement, isUserFableticsEligible, userHasGlobalPrivileges } from '../../utils/user';
import { useStatsig } from './useStatsig';

// Single source of truth for the Fabletics module. Returns whether the module
// renders (`enabled`) and which creative the viewer should see, expressed as a
// user-data decision: `isUpsellAudience` is true only for the acquisition
// audience — US Free / Power Monthly viewers who hold no Fabletics or Global
// Privileges perk yet — who see the upsell creative. Everyone else who renders
// the module already has a perk and sees the member creative.
//
// Audience routing:
// - Fabletics-entitled (any plan with ctaUrl): member creative — entitlement
//   overrides country and tier.
// - Annual subscribers: member creative, any country (an annual plan always
//   grants Global Privileges access).
// - Non-annual users with a Global Privileges plan: member creative, any country.
// - US non-annual without any perk: the upsell creative.
// - Anyone else: enabled=false (consumers fall back to GlobalPrivilegesBanner).

const useFabletics = () => {
    const user = useSelector(({ auth }) => auth?.user);
    const { checkGate, featureGates } = useStatsig();

    if (!checkGate(featureGates.FABLETICS_POWER_BENEFIT)) {
        return { enabled: false, isUpsellAudience: false };
    }

    if (getFableticsEntitlement(user)?.ctaUrl) {
        return { enabled: true, isUpsellAudience: false };
    }

    const isAnnualSubscriber = !!user?.isPowered &&
        user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR;
    const isUSEligible = isUserFableticsEligible(user);

    if (isAnnualSubscriber) {
        return { enabled: true, isUpsellAudience: false };
    }

    if (userHasGlobalPrivileges(user)) {
        return { enabled: true, isUpsellAudience: false };
    }

    if (!isUSEligible) {
        return { enabled: false, isUpsellAudience: false };
    }

    return { enabled: true, isUpsellAudience: true };
};

export default useFabletics;
